import React, { useState, useCallback, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRealtime } from '../../hooks/useRealtime';
import NotificationCenter from '../common/NotificationCenter';
import { LogoUNEA } from '../common/Logo';
import { useTenant, useT } from '../../context/TenantContext';
import MIcon from '../common/MIcon';
import Icon from '../common/Icon';
import WhatsNew from '../common/WhatsNew';
import { useWhatsNew } from '../../hooks/useWhatsNew';

const NAV_ICONS = {
  dashboard: 'dashboard',
  tickets:   'confirmation_number',
  agents:    'group',
  computers: 'computer',
  sla:       'timer',
  external:  'open_in_new',
};

const menuItemStyle = {
  display:'flex', alignItems:'center', gap:10, width:'100%', padding:'8px 16px',
  background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#374151',
  textAlign:'left', transition:'background .12s',
};

const NavIcon = ({ name }) => (
  <Icon name={NAV_ICONS[name] || name} size={18} style={{color:'currentColor'}} />
);

const LANGS = [
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'pt', flag: '🇦🇴', label: 'Português' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
];

export default function BackDeskLayout({ children, title, actions }) {
  const { agent, logout } = useAuth();
  const { t } = useT();
  const tenant = useTenant();
  const navigate          = useNavigate();
  const [onlineAgents,    setOnlineAgents]    = useState([]);
  const [ncRef,           setNcRef]           = useState(null);
  const [collapsed,       setCollapsed]       = useState(() => localStorage.getItem('bd_sidebar_collapsed') === '1');
  const { show: showWN, changelog, version, dismiss: dismissWN, open: openWN, hasUnseen } = useWhatsNew();

  function toggleCollapsed() {
    setCollapsed(c => {
      localStorage.setItem('bd_sidebar_collapsed', c ? '0' : '1');
      return !c;
    });
  }

  const handleEvent = useCallback((event) => {
    if (event.type === 'agents_online' || event.type === 'agent_joined' || event.type === 'agent_left') {
      setOnlineAgents(event.agents || []);
    }
    // No notificarte a ti mismo tu propio comentario
    if (event.type === 'comment_added' && event.comment?.author_name === (agent?.full_name || agent?.username)) {
      return;
    }
    // Reenviar al NotificationCenter
    if (NotificationCenter.addNotification) {
      NotificationCenter.addNotification(event);
    }
  }, [agent]);

  useRealtime(handleEvent);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const langOverride = localStorage.getItem('bd_lang_override');
  const currentLang  = langOverride || tenant?.lang || 'es';

  function setLang(code) {
    localStorage.setItem('bd_lang_override', code);
    window.location.reload();
  }

  useEffect(() => {
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleLogout() { logout(); navigate('/login'); }

  const initials = agent?.full_name?.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || '??';

  return (
    <div className="bd-root">
      {showWN && <WhatsNew changelog={changelog} version={version} onDismiss={dismissWN}/>}
      {/* AppBar full-width */}
      <header className="bd-appbar" style={{borderBottomColor: tenant?.primaryColor || "#0288D1"}}>
        <div style={{display:'flex',alignItems:'center',gap:0}}>
          <LogoUNEA height={32} style={{display:'block'}} />
          <div style={{width:1,height:28,background:'var(--bd-border)',margin:'0 16px'}}/>
          <span style={{fontSize:15,fontWeight:400,color:'var(--bd-text)'}}>{tenant?.appName || t('nav.dashboard')}</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          {/* What's new button */}
          <button onClick={openWN} title="Novedades" style={{
            position:'relative', background:'none', border:'none', cursor:'pointer',
            padding:6, borderRadius:8, display:'flex', alignItems:'center', color:'var(--bd-muted)',
          }}>
            <MIcon name="new_releases" size={20}/>
            {hasUnseen && (
              <span style={{
                position:'absolute', top:4, right:4, width:8, height:8,
                borderRadius:'50%', background:'#EF4444', border:'2px solid #fff',
              }}/>
            )}
          </button>
          <NotificationCenter onlineAgents={onlineAgents}/>
          <div ref={userMenuRef} style={{position:'relative'}}>
            <button onClick={() => setUserMenuOpen(o => !o)} style={{
              display:'flex', alignItems:'center', gap:8, background:'none', border:'none',
              cursor:'pointer', padding:'4px 6px', borderRadius:8,
              transition:'background .15s',
            }}
            onMouseEnter={e=>e.currentTarget.style.background='var(--bd-hover,#f3f4f6)'}
            onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <div className="bd-user-avatar">{initials}</div>
              <div style={{textAlign:'left'}}>
                <div style={{fontSize:12,fontWeight:600,color:'var(--bd-text)'}}>{agent?.full_name || agent?.username}</div>
                <div style={{fontSize:10,color:'var(--bd-muted)'}}>{agent?.role} · N{agent?.max_level}</div>
              </div>
              <MIcon name={userMenuOpen ? 'expand_less' : 'expand_more'} size={18} style={{color:'var(--bd-muted)',marginLeft:2}}/>
            </button>

            {userMenuOpen && (
              <div style={{
                position:'absolute', right:0, top:'calc(100% + 8px)', zIndex:999,
                background:'#fff', borderRadius:10, border:'1px solid #E5E7EB',
                boxShadow:'0 4px 24px rgba(0,0,0,.10)', minWidth:200, overflow:'hidden',
                padding:'6px 0',
              }}>
                {/* Header */}
                <div style={{padding:'10px 16px 8px', borderBottom:'1px solid #F3F4F6', marginBottom:4}}>
                  <div style={{fontSize:13,fontWeight:600,color:'#111827'}}>{agent?.full_name || agent?.username}</div>
                  <div style={{fontSize:11,color:'#6B7280'}}>{agent?.role} · N{agent?.max_level}</div>
                </div>

                {/* Ir al portal */}
                <button onClick={() => { setUserMenuOpen(false); navigate('/'); }} style={menuItemStyle}>
                  <MIcon name="open_in_new" size={16} style={{color:'#6B7280'}}/>
                  {t('general.user_portal')}
                </button>
                <a href="/extensions" target="_blank" rel="noopener noreferrer"
                  onClick={() => setUserMenuOpen(false)}
                  style={{...menuItemStyle, textDecoration:'none', display:'flex', alignItems:'center', gap:8}}>
                  <MIcon name="phone" size={16} style={{color:'#6B7280'}}/>
                  {t('general.public_dir')}
                </a>
                <a href="/rooms" target="_blank" rel="noopener noreferrer"
                  onClick={() => setUserMenuOpen(false)}
                  style={{...menuItemStyle, textDecoration:'none', display:'flex', alignItems:'center', gap:8}}>
                  <MIcon name="meeting_room" size={16} style={{color:'#6B7280'}}/>
                  {t('general.room_booking')}
                </a>

                <div style={{height:1, background:'#F3F4F6', margin:'4px 0'}}/>

                {/* Idioma */}
                <div style={{padding:'6px 16px 4px', fontSize:10, fontWeight:700, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:.5}}>
                  {t('general.language')}
                </div>
                <div style={{display:'flex', gap:4, padding:'2px 10px 6px'}}>
                  {LANGS.map(l => (
                    <button key={l.code} onClick={() => setLang(l.code)} style={{
                      flex:1, padding:'5px 4px', borderRadius:7, border:'1px solid',
                      borderColor: currentLang === l.code ? 'var(--una-blue)' : '#E5E7EB',
                      background: currentLang === l.code ? 'var(--una-blue-lt)' : '#fff',
                      cursor:'pointer', fontSize:12, fontWeight: currentLang === l.code ? 700 : 400,
                      color: currentLang === l.code ? 'var(--una-blue)' : '#6B7280',
                      display:'flex', flexDirection:'column', alignItems:'center', gap:1,
                    }}>
                      <span style={{fontSize:16}}>{l.flag}</span>
                      <span>{l.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>

                <div style={{height:1, background:'#F3F4F6', margin:'4px 0'}}/>

                {/* Cerrar sesión */}
                <button onClick={() => { setUserMenuOpen(false); handleLogout(); }} style={{...menuItemStyle, color:'#EF4444'}}>
                  <MIcon name="logout" size={16} style={{color:'#EF4444'}}/>
                  {t('general.logout')}
                </button>

                <div style={{height:1, background:'#F3F4F6', margin:'4px 0'}}/>

                {/* Versión */}
                <div style={{padding:'6px 16px', fontSize:11, color:'#9CA3AF'}}>
                  v{version || '—'}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="bd-body">
      <aside className={`bd-sidebar ${collapsed ? 'collapsed' : ''}`}>
        <button onClick={toggleCollapsed} title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          style={{
            display:'flex', alignItems:'center', justifyContent: collapsed ? 'center' : 'flex-end',
            padding: collapsed ? '10px 0' : '8px 12px',
            border:'none', borderBottom:'1px solid var(--bd-border)', background:'transparent',
            cursor:'pointer', color:'var(--bd-muted)',
          }}>
          <MIcon name={collapsed ? 'chevron_right' : 'chevron_left'} size={18}/>
        </button>
        <nav className="bd-nav">
          {/* Helper: comprueba si un módulo está activo en el tenant */}
          {(() => {
            const mods = tenant?.modules;
            // Sin módulos definidos (modo legacy o superadmin) → todo activo
            const hasModule = (id) => !mods || mods.find(m => m.module === id && m.active);
            const isAdmin   = agent?.role === 'admin' || agent?.role === 'superadmin';
            const notReceptionist = agent?.role !== 'receptionist';

            return (<>
              {/* Core: siempre visibles (no receptionist) */}
              {notReceptionist && (<>
                <NavLink to="/backdesk" end title={t('nav.dashboard')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <NavIcon name="dashboard"/> {t('nav.dashboard')}
                </NavLink>
                <NavLink to="/backdesk/tickets" title={t('nav.tickets')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <NavIcon name="tickets"/> {t('nav.tickets')}
                </NavLink>
              </>)}

              {/* Calendario */}
              {notReceptionist && hasModule('calendario') && (
                <NavLink to="/backdesk/calendar" title={t('nav.calendar')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <MIcon name="calendar_month" size={18}/> {t('nav.calendar')}
                </NavLink>
              )}

              {/* Equipos */}
              {notReceptionist && hasModule('equipos') && (
                <NavLink to="/backdesk/computers" title="Equipos" className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <NavIcon name="computers"/>
                  <span style={{flex:1}}>Equipos</span>
                </NavLink>
              )}

              {/* Agentes */}
              {notReceptionist && hasModule('agentes') && (
                <NavLink to="/backdesk/agents" title="Agentes" className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <NavIcon name="agents"/>
                  <span style={{flex:1}}>Agentes</span>
                  {onlineAgents.length > 0 && (
                    <span style={{fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:10,background:'#F0FDF4',color:'#15803D',border:'1px solid #86EFAC'}}>
                      {onlineAgents.length}
                    </span>
                  )}
                </NavLink>
              )}

              {/* SLA */}
              {isAdmin && hasModule('sla') && (
                <NavLink to="/backdesk/sla" title={t('nav.sla')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <NavIcon name="sla"/> {t('nav.sla')}
                </NavLink>
              )}

              {/* Encuestas */}
              {isAdmin && hasModule('encuestas') && (
                <NavLink to="/backdesk/surveys" title={t('nav.surveys')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <Icon name="star" size={18} style={{color:'currentColor'}}/> {t('nav.surveys')}
                </NavLink>
              )}

              {/* Clientes */}
              {isAdmin && hasModule('clientes') && (
                <NavLink to="/backdesk/clients" title={t('nav.clients')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <Icon name="people" size={18} style={{color:'currentColor'}}/> {t('nav.clients')}
                </NavLink>
              )}

              {/* Derivaciones */}
              {isAdmin && hasModule('derivaciones') && (
                <NavLink to="/backdesk/departments" title={t('nav.departments')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <MIcon name="forward_to_inbox" size={18}/> {t('nav.departments')}
                </NavLink>
              )}

              {/* Extensiones */}
              {(isAdmin || agent?.role === 'receptionist') && hasModule('extensiones') && (
                <NavLink to="/backdesk/extensions" title={t('nav.extensions')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <MIcon name="phone" size={18}/> {t('nav.extensions')}
                </NavLink>
              )}

              {/* Reservas */}
              {isAdmin && hasModule('reservas') && (
                <NavLink to="/backdesk/rooms" title={t('nav.rooms')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <MIcon name="meeting_room" size={18}/> {t('nav.rooms')}
                </NavLink>
              )}

              {/* Préstamos */}
              {isAdmin && hasModule('prestamos') && (
                <NavLink to="/backdesk/loans" title="Préstamos" className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <MIcon name="inventory_2" size={18}/> Préstamos
                </NavLink>
              )}

              {/* Informes */}
              {isAdmin && hasModule('informes') && (
                <NavLink to="/backdesk/reports" title={t('nav.reports')} className={({isActive}) => `bd-nav-item ${isActive?'active':''}`}>
                  <MIcon name="bar_chart" size={18}/> {t('nav.reports')}
                </NavLink>
              )}

              {/* SuperAdmin */}
              {agent?.role === 'superadmin' && (
                <a href="/superadmin" title={t('nav.superadmin')} className="bd-nav-item" style={{borderTop:'1px solid var(--bd-border)',marginTop:8,paddingTop:12,color:'#7C3AED'}}>
                  <MIcon name="admin_panel_settings" size={18}/> {t('nav.superadmin')}
                </a>
              )}
            </>);
          })()}
        </nav>


      </aside>

      <div className="bd-main">
        {(title || actions) && (
          <div className="bd-topbar">
            <span className="bd-topbar-title">{title}</span>
            <div className="bd-topbar-actions">{actions}</div>
          </div>
        )}
        <div className="bd-content">{children}</div>
      </div>
      </div>
    </div>
  );
}
